import { useState, useEffect } from "react";
import { type StreakResponseV2, type ScheduledCycle, AUTOMATIC_STREAK_STATUS } from "@cede/types";

/**
 * Check if a cycle is currently being executed for V2 streaks.
 * A cycle is considered "executing" if:
 * 1. Its scheduledAt time has passed (now >= scheduledAt)
 * 2. It hasn't finished yet (no finishedAt)
 * 3. It hasn't been skipped
 *
 * @returns The executing scheduled cycle or null if no cycle is executing
 */
const getExecutingCycle = (
  streak: StreakResponseV2,
): NonNullable<NonNullable<StreakResponseV2["executionLog"]>["scheduledCycles"]>[number] | null => {
  // Only check for in-progress streaks
  if (streak.status !== AUTOMATIC_STREAK_STATUS.IN_PROGRESS) {
    return null;
  }

  const scheduledCycles = streak.executionLog?.scheduledCycles;
  if (!scheduledCycles || scheduledCycles.length === 0) {
    return null;
  }

  const now = Date.now();

  // Find a cycle that:
  // - Has scheduledAt in the past (or now)
  // - Has not finished (no finishedAt)
  // - Has not been skipped
  const executingCycle = scheduledCycles.find((cycle) => {
    const scheduledAt = new Date(cycle.scheduledAt).getTime();
    const hasStarted = now >= scheduledAt;
    const hasNotFinished = !cycle.finishedAt;
    const hasNotBeenSkipped = !cycle.isSkipped;

    return hasStarted && hasNotFinished && hasNotBeenSkipped;
  });

  return executingCycle ?? null;
};

/**
 * Hook to track if a cycle is currently being executed for V2 streaks.
 * Updates every second to check if a scheduled cycle has started but not finished.
 *
 * A cycle is considered "executing" if:
 * 1. Its scheduledAt time has passed (now >= scheduledAt)
 * 2. It hasn't finished yet (no finishedAt)
 * 3. It hasn't been skipped
 *
 * @param streak - The V2 streak to monitor
 * @returns The executing scheduled cycle or null if no cycle is executing
 */
export const useExecutingCycle = (streak: StreakResponseV2): ScheduledCycle | null => {
  const [executingCycle, setExecutingCycle] = useState<ScheduledCycle | null>(null);

  useEffect(() => {
    const checkExecutingCycle = () => {
      setExecutingCycle(getExecutingCycle(streak));
    };

    checkExecutingCycle();
    const interval = setInterval(checkExecutingCycle, 1000);

    return () => clearInterval(interval);
  }, [streak]);

  return executingCycle;
};
